/**
 * SnippetsPreview.tsx
 * Review Snippets widget preview component
 * Shows 5x2 photo grid with rotating review text
 * Path: components/admin-tabs/reviews/display-settings/previews/SnippetsPreview.tsx
 */

import React, { useState, useEffect } from 'react';
import { Review, WidgetSettings } from '../../../../../types';
import { Star } from 'lucide-react';

interface SnippetsPreviewProps {
  reviews: Review[];
  settings: Partial<WidgetSettings>;
}

export const SnippetsPreview: React.FC<SnippetsPreviewProps> = ({ reviews, settings }) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  // Filter reviews with photos
  const reviewsWithPhotos = reviews.filter(r => r.media_urls && r.media_urls.length > 0);

  // Apply photo source filter
  let filteredReviews = reviewsWithPhotos;
  if (settings.snippets_photo_source === 'verified') {
    filteredReviews = reviewsWithPhotos.filter(r => r.verified_purchase);
  } else if (settings.snippets_photo_source === 'top_rated') {
    filteredReviews = reviewsWithPhotos.filter(r => r.rating >= 4);
  } else if (settings.snippets_photo_source === 'recent') {
    filteredReviews = [...reviewsWithPhotos].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }

  // Get number of photos to show
  const photosToShow = settings.snippets_photos || 10;
  const selectedReviews = filteredReviews.slice(0, photosToShow);

  // Determine grid layout
  const layout = settings.snippets_layout || '5x2';
  const gridCols = layout === '5x2' ? 'grid-cols-5' : 
                   layout === '4x2' ? 'grid-cols-4' : 
                   'grid-cols-3';

  // Text rotation effect
  useEffect(() => {
    if (selectedReviews.length === 0) return;
    
    const speed = settings.snippets_speed || 4000;
    const interval = setInterval(() => {
      setCurrentTextIndex(prev => (prev + 1) % selectedReviews.length);
    }, speed);

    return () => clearInterval(interval);
  }, [selectedReviews.length, settings.snippets_speed]);

  // Container style
  const containerStyle = settings.snippets_container_style || 'bordered';
  const containerClasses = containerStyle === 'shadow' 
    ? 'shadow-lg border-transparent' 
    : 'border border-gray-200';

  const roundedClass = settings.snippets_rounded_corners !== false ? 'rounded-xl' : 'rounded-none';

  if (selectedReviews.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-4xl mb-3">📸</div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">No Photos Available</h4>
        <p className="text-sm text-gray-600">
          Reviews need to have customer photos to display in snippets widget
        </p>
      </div>
    );
  }

  const currentReview = selectedReviews[currentTextIndex];

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5 justify-center">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className="w-4 h-4"
            style={{
              fill: star <= rating ? (settings.star_color || '#fbbf24') : 'none',
              color: star <= rating ? (settings.star_color || '#fbbf24') : '#e5e7eb'
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={`bg-gray-50 p-6 ${containerClasses} ${roundedClass}`}>
      
      {/* Title */}
      <div className="text-center mb-4">
        <h3 className="text-lg font-bold text-gray-900">Customers Proof</h3>
      </div>

      {/* Photo Grid */}
      <div className={`grid ${gridCols} gap-2 mb-5`}>
        {selectedReviews.map((review, index) => (
          <div
            key={review.id}
            className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden group cursor-pointer transition-transform hover:scale-105 hover:z-10 shadow-sm"
          >
            <img
              src={review.media_urls[0].url}
              alt={`Review by ${review.reviewer_name}`}
              className="w-full h-full object-cover"
            />
            
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center">
              <span className="text-2xl opacity-0 group-hover:opacity-100 transition-opacity">
                👁️
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Rotating Review Text */}
      <div className="text-center px-4 py-3 bg-white rounded-lg min-h-[100px] flex flex-col items-center justify-center">
        <div 
          key={currentTextIndex}
          className="animate-fadeIn"
        >
          {/* Stars */}
          {settings.snippets_show_stars !== false && (
            <div className="mb-2">
              {renderStars(currentReview.rating)}
            </div>
          )}

          {/* Review Text */}
          <p className="text-sm text-gray-700 italic line-clamp-3 mb-2">
            "{currentReview.content}"
          </p>

          {/* Customer Name */}
          {settings.snippets_show_names !== false && (
            <p className="text-xs text-gray-500 font-medium">
              - {currentReview.reviewer_name}
            </p>
          )}
        </div>
      </div>

      {/* Indicator Dots */}
      <div className="flex justify-center gap-1.5 mt-4">
        {selectedReviews.slice(0, Math.min(5, selectedReviews.length)).map((_, index) => (
          <div
            key={index}
            className={`w-1.5 h-1.5 rounded-full transition-all ${
              index === currentTextIndex % 5
                ? 'bg-blue-600 w-4'
                : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Preview Info */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          <span className="font-medium">{selectedReviews.length}</span> photos • 
          Rotating every <span className="font-medium">{(settings.snippets_speed || 4000) / 1000}s</span> • 
          Layout: <span className="font-medium">{layout}</span>
        </p>
      </div>

      {/* Add fadeIn animation styles */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};
